/**
 * The **API token** panel, drawn from the capture in
 * `discovery/ui-reference/developers/INDEX.md` (M9 §8).
 *
 * monday's page is deliberately small: one heading, one paragraph, **one** token in a 48 px
 * bordered row with `Regenerate · Copy · Show` right-aligned, and a grey note bar. There is no
 * list, no name, no "create" button — a user has exactly one personal token and it already
 * exists.
 *
 * Three places where ours differs, each on purpose and each recorded in the capture's §3:
 *
 * | # | Difference | Why |
 * |---|---|---|
 * | 1 | A **Generate** state when no token exists | monday's always exists; ours is created on demand, and the alternative would be minting a secret on a page load. |
 * | 2 | Created / last-used / last API version under the row | A useful superset. It is how "who still sends 2026-07?" becomes answerable before a version is retired. |
 * | 3 | **Regenerate asks first** | monday's confirmation was not captured (pressing it would have invalidated the token a production cron job holds). Asking is the conservative reading. |
 *
 * `Show` re-reads the value from an encrypted-at-rest copy, which is exactly what monday's own
 * *Show* proves is possible (capture §3 row 4, OQ-10). Without `TOKEN_ENCRYPTION_KEY` the
 * button is disabled with the reason on it, rather than hidden.
 */
import { formatMondayDateTime } from '@elizmonday/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import type * as React from 'react';
import { useMemo, useState } from 'react';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import type { TokenSource, TokenSummary } from './source';
import './developer.css';

/** monday masks the value with **37** asterisks, with no prefix and no visible tail. */
export const MONDAY_MASK = '*'.repeat(37);

function formatMoment(value: string | null): string {
  if (value === null) return 'never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : formatMondayDateTime(date);
}

/**
 * The tRPC-backed source. `account.tokens.*` is M0-F3's, unchanged: this page adds no
 * procedure of its own.
 */
function useTrpcTokenSource(): TokenSource {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const capabilitiesOptions = trpc.account.capabilities.queryOptions();
  const listOptions = trpc.account.tokens.list.queryOptions({});
  return useMemo<TokenSource>(
    () => ({
      capabilities: async () => {
        const result = await client.account.capabilities.query();
        const sidebar = result.sidebar;
        return {
          canCreate: sidebar?.canCreateApiToken ?? false,
          notice: sidebar?.apiTokenNotice ?? 'none',
        };
      },
      list: async () => (await client.account.tokens.list.query({})) as TokenSummary[],
      create: async () => client.account.tokens.create.mutate({ name: 'Personal token' }),
      show: (id) => client.account.tokens.show.mutate({ id }),
      regenerate: (id) => client.account.tokens.regenerate.mutate({ id }),
    }),
    // `queryOptions()` is stable per procedure; the keys keep the memo honest if that changes.
    [client, capabilitiesOptions.queryKey, listOptions.queryKey],
  ) as TokenSource;
}

export interface ApiTokenPanelProps {
  /** Injected by the tests and the `/__ui` harness; production uses tRPC. */
  source?: TokenSource;
}

/**
 * `useTRPC()` throws outside a provider, and a hook cannot be called conditionally — so the
 * choice is made by **rendering** one of two components, not by branching inside one.
 */
export function ApiTokenPanel({ source }: ApiTokenPanelProps = {}): React.ReactElement {
  return source ? <TokenPanelView source={source} /> : <TrpcTokenPanel />;
}

function TrpcTokenPanel(): React.ReactElement {
  return <TokenPanelView source={useTrpcTokenSource()} />;
}

function TokenPanelView({ source }: { source: TokenSource }): React.ReactElement {
  const capabilities = useQuery({ queryKey: ['developer', 'capabilities'], queryFn: () => source.capabilities() });
  const tokens = useQuery({ queryKey: ['developer', 'tokens'], queryFn: () => source.list() });

  const [revealed, setRevealed] = useState<string | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fail = (caught: unknown): void =>
    setError((caught as { message?: string }).message ?? 'That did not work.');

  const create = useMutation({
    mutationFn: () => source.create(),
    onSuccess: async (result) => {
      setFresh(result.value);
      setRevealed(result.value);
      await tokens.refetch();
    },
    onError: fail,
  });

  const show = useMutation({
    mutationFn: (id: string) => source.show(id),
    onSuccess: (result) => setRevealed(result.value),
    onError: fail,
  });

  const regenerate = useMutation({
    mutationFn: (id: string) => source.regenerate(id),
    onSuccess: async (result) => {
      setFresh(result.value);
      setRevealed(result.value);
      await tokens.refetch();
    },
    onError: fail,
  });

  const rows = tokens.data ?? [];
  const token = rows.find((row) => row.revokedAt === null) ?? rows[0] ?? null;
  const notice = capabilities.data?.notice ?? 'none';
  const canCreate = capabilities.data?.canCreate ?? false;

  const copy = (): void => {
    if (revealed === null) return;
    void navigator.clipboard?.writeText(revealed);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div data-testid="developers-page">
      <h1 className="ez-dev__heading">API token</h1>
      <p className="ez-dev__lede">
        This GraphQL API token belongs only to you. It grants access to every query and mutation
        you’re allowed to perform. Send it in the <code>Authorization</code> header{' '}
        <strong>raw, with no “Bearer” prefix</strong>.
      </p>

      {notice === 'guest-cannot-create' ? (
        <div className="ez-dev__note ez-dev__note--warning" data-testid="developers-guest-notice">
          Guests cannot create API tokens.
        </div>
      ) : null}
      {notice === 'viewer-token-refused' ? (
        <div className="ez-dev__note ez-dev__note--warning" data-testid="developers-viewer-notice">
          Viewers cannot use the API: a token created here would be refused by <code>/v2</code>.
        </div>
      ) : null}

      {token === null ? (
        <div className="ez-dev__row" data-testid="token-empty">
          <span className="ez-dev__value">You do not have a personal API token yet.</span>
          <div className="ez-dev__actions">
            <button
              type="button"
              className="ez-dev__action"
              disabled={!canCreate || create.isPending}
              title={canCreate ? undefined : 'Your account role cannot create API tokens.'}
              onClick={() => {
                setError(null);
                create.mutate();
              }}
              data-testid="token-create"
            >
              Generate
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="ez-dev__row" data-testid={`token-${token.id}`}>
            <span
              className={`ez-dev__value${revealed === null ? '' : ' ez-dev__value--revealed'}`}
              data-testid="token-value"
            >
              {revealed ?? MONDAY_MASK}
            </span>
            <div className="ez-dev__actions">
              <button
                type="button"
                className="ez-dev__action"
                onClick={() => {
                  setError(null);
                  if (
                    window.confirm(
                      'Regenerate this token? Every integration using the old value stops working immediately.',
                    )
                  ) {
                    regenerate.mutate(token.id);
                  }
                }}
                data-testid="token-regenerate"
              >
                Regenerate
              </button>
              <button
                type="button"
                className="ez-dev__action"
                disabled={revealed === null}
                title={revealed === null ? 'Reveal the token first.' : undefined}
                onClick={copy}
                data-testid="token-copy"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button
                type="button"
                className="ez-dev__action"
                disabled={!token.canShow || revealed !== null}
                title={token.canShow ? undefined : 'TOKEN_ENCRYPTION_KEY is not configured on this server.'}
                onClick={() => {
                  setError(null);
                  show.mutate(token.id);
                }}
                data-testid="token-show"
              >
                Show
              </button>
            </div>
          </div>

          <div className="ez-dev__note" data-testid="token-note">
            Note: always keep this token secret and secure.
          </div>

          <p className="ez-dev__meta" data-testid="token-meta">
            Created {formatMoment(token.createdAt)} · Last used {formatMoment(token.lastUsedAt)} ·
            Last API version {token.lastSeenApiVersion ?? '—'}
            {token.revokedAt === null ? '' : ' · revoked'}
          </p>
        </>
      )}

      {error === null ? null : (
        <p className="ez-dev__error" data-testid="token-error">
          {error}
        </p>
      )}

      {fresh === null ? null : (
        <section className="ez-dev__panel" data-testid="token-once">
          <h2>Your new token</h2>
          <p className="ez-dev__meta">
            Copy it now. It stays readable through <strong>Show</strong> while this server keeps an
            encrypted copy, and nowhere else.
          </p>
          <pre className="ez-dev__snippet" data-testid="token-curl">
            {`curl -s ${window.location.origin}/v2 \\
  -H 'Content-Type: application/json' \\
  -H 'API-Version: 2026-10' \\
  -H 'Authorization: ${fresh}' \\
  -d '{"query":"{ me { id name } }"}'`}
          </pre>
          <button
            type="button"
            className="ez-dev__action"
            /*
             * *Done* means "I have copied it": the row goes back to monday's mask, and `Show`
             * is how you see it again (`discovery/ui-reference/developers/INDEX.md` — the page
             * ships the value masked until you click Show). Leaving it revealed after the panel
             * is dismissed left the raw token on screen for the rest of the session.
             */
            onClick={() => {
              setFresh(null);
              setRevealed(null);
            }}
            data-testid="token-once-dismiss"
          >
            Done
          </button>
        </section>
      )}
    </div>
  );
}
